import { useEffect, useState } from "react";
import DeliveryChannelCard from "./DeliveryChannelCard";
import ChannelSequenceSummary from "./ChannelSequenceSummary";
import {
  DeliveryService,
  type LaunchDeliveryConfig,
  type ProviderAccount,
} from "@services/delivery/deliveryService";

/*
    Switching SMS on for a campaign (MPN-1717).

    It sits beside Publishers rather than inside it, for the same reason mail and email do:
    SMS is not a publisher. There is no ad account, no bidding and no template to import.

    What it needs instead is whose Twilio account sends it and the hours it may send in, and
    the second of those has no equivalent anywhere else in this screen. The TCPA restricts
    automated marketing texts to 8am-9pm and prices a violation per message, so the window is
    a field on the campaign rather than a global setting: a client whose students are mostly
    in one state wants that state's clock.

    What this window cannot yet do is worth being honest about on the card itself. It is one
    timezone for the whole campaign, so a recipient in Hawaii on an Eastern window can be
    texted at 2am their time and still be inside it. Closing that needs per-recipient
    timezones, which are being built with the trigger work in MPN-1747.
*/

const CHANNEL = "sms";

// 8am to 9pm is what everybody means by quiet hours, and what the TCPA settled on.
const DEFAULT_WINDOW = { start_hour: 8, end_hour: 21, timezone: "America/New_York" };

// The five the platform's clients actually enrol from. A free-text field here is a way to
// get "EST" saved and silently fall back to the default.
const ZONES = [
  ["America/New_York", "Eastern"],
  ["America/Chicago", "Central"],
  ["America/Denver", "Mountain"],
  ["America/Los_Angeles", "Pacific"],
  ["America/Anchorage", "Alaska"],
  ["Pacific/Honolulu", "Hawaii"],
];

const HOURS = Array.from({ length: 25 }, (_, h) => h);

const hourLabel = (h: number) => {
  if (h === 0) return "midnight";
  if (h === 12) return "noon";
  if (h === 24) return "midnight";
  return h < 12 ? `${h}am` : `${h - 12}pm`;
};

interface SmsCardProps {
  launchId: number;
  engagementId: number;
  organizationId?: number | null;
}

export default function SmsCard({ launchId, engagementId, organizationId }: SmsCardProps) {
  const [config, setConfig] = useState<LaunchDeliveryConfig | null>(null);
  const [accounts, setAccounts] = useState<ProviderAccount[]>([]);
  const [window, setWindow] = useState(DEFAULT_WINDOW);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const service = new DeliveryService();

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      service.listLaunchChannels(launchId),
      organizationId ? service.listAccounts(organizationId) : Promise.resolve([]),
    ])
      .then(([channels, orgAccounts]) => {
        if (cancelled) return;
        const found = channels.find((c) => c.channel === CHANNEL) ?? null;
        setConfig(found);
        setWindow({
          start_hour: found?.send_window?.start_hour ?? DEFAULT_WINDOW.start_hour,
          end_hour: found?.send_window?.end_hour ?? DEFAULT_WINDOW.end_hour,
          timezone: found?.send_window?.timezone ?? DEFAULT_WINDOW.timezone,
        });
        // Only Twilio accounts. An organisation can hold a Lob and an SES account too, and
        // offering those here would let somebody pick a printer to send texts with.
        setAccounts(orgAccounts.filter((a) => a.provider === "twilio"));
      })
      .catch(() => !cancelled && setConfig(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [launchId, organizationId]);

  const save = async (patch: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      setConfig(await service.configureChannel(launchId, CHANNEL, patch as any));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the SMS settings.");
    } finally {
      setBusy(false);
    }
  };

  const saveWindow = (patch: Partial<typeof DEFAULT_WINDOW>) => {
    const next = { ...window, ...patch };
    setWindow(next);
    save({ send_window: next });
  };

  const toggle = async (nextEnabled: boolean) => {
    setBusy(true);
    setError(null);
    try {
      setConfig(await service.toggleChannel(launchId, CHANNEL, nextEnabled));
    } catch (e) {
      // The server refuses an incomplete channel and says what is missing. That message is
      // more useful than anything this component could invent.
      setError(e instanceof Error ? e.message : "Could not change SMS.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  const enabled = !!config?.is_enabled;
  const inverted = window.start_hour >= window.end_hour;

  return (
    <>
      <DeliveryChannelCard label="Text messages" enabled={enabled} error={error}>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <div className="fw-semibold">
              <i className="bi bi-chat-dots me-2" style={{ color: "#22c55e" }}></i>
              One text per person
            </div>
            <div className="text-muted small">
              Sent when this campaign publishes, to everyone with a mobile number who is not in
              the holdout and has not texted STOP.
            </div>
          </div>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="sms-toggle"
              aria-label="Text messages"
              checked={enabled}
              disabled={busy}
              onChange={(e) => toggle(e.target.checked)}
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label small text-muted mb-1" htmlFor="sms-account">
            Account that sends it
          </label>
          <select
            id="sms-account"
            className="form-select form-select-sm"
            value={config?.provider_account_id ?? ""}
            disabled={busy}
            onChange={(e) =>
              save({ provider_account_id: e.target.value ? Number(e.target.value) : null })
            }
          >
            <option value="">Motimatic&apos;s number</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
          <div className="form-text">
            Motimatic&apos;s registered number unless this client has brought their own. A
            client&apos;s own number needs its own A2P 10DLC registration before carriers will
            deliver anything from it.
          </div>
        </div>

        <div>
          <div className="small text-muted mb-2">
            Hours texts may go out in. Nothing automated is sent outside these, and a campaign
            that publishes outside them waits rather than failing.
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-end">
            <div style={{ minWidth: 120 }}>
              <label className="form-label small text-muted mb-1" htmlFor="sms-window-start">
                From
              </label>
              <select
                id="sms-window-start"
                className="form-select form-select-sm"
                value={window.start_hour}
                disabled={busy}
                onChange={(e) => saveWindow({ start_hour: Number(e.target.value) })}
              >
                {HOURS.slice(0, 24).map((h) => (
                  <option key={h} value={h}>
                    {hourLabel(h)}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ minWidth: 120 }}>
              <label className="form-label small text-muted mb-1" htmlFor="sms-window-end">
                Until
              </label>
              <select
                id="sms-window-end"
                className="form-select form-select-sm"
                value={window.end_hour}
                disabled={busy}
                onChange={(e) => saveWindow({ end_hour: Number(e.target.value) })}
              >
                {HOURS.slice(1).map((h) => (
                  <option key={h} value={h}>
                    {hourLabel(h)}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ minWidth: 160, flex: "1 1 auto" }}>
              <label className="form-label small text-muted mb-1" htmlFor="sms-window-zone">
                Read in
              </label>
              <select
                id="sms-window-zone"
                className="form-select form-select-sm"
                value={window.timezone}
                disabled={busy}
                onChange={(e) => saveWindow({ timezone: e.target.value })}
              >
                {ZONES.map(([value, name]) => (
                  <option key={value} value={value}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {inverted && (
            <div className="alert alert-warning py-2 small mt-2 mb-0">
              The end has to be later than the start. Until it is, the legal default of 8am to
              9pm applies.
            </div>
          )}

          <div className="form-text mt-2">
            One timezone for the whole campaign, so set it to wherever most of this
            client&apos;s students are. Texting each person in their own local time needs the
            per-recipient timezone work in MPN-1747.
          </div>
        </div>
        <ChannelSequenceSummary
          launchId={launchId}
          engagementId={engagementId}
          channel="sms"
          enabled={enabled}
        />
      </DeliveryChannelCard>
    </>
  );
}
